/**
 * Dashboard data hook for AI Recruit
 * Manages dashboard analytics data fetching and state with filtering support
 */

import { useState, useEffect } from 'react'
import { analyticsApi } from '../services/api'
import { usePermission } from './usePermission'

export type DateRangeOption = 'today' | 'last_7_days' | 'last_30_days' | 'this_month' | 'custom'

export interface TrendData {
  value: number
  type: 'percentage' | 'absolute'
  direction: 'up' | 'down' | 'neutral'
  comparison_period: string
}

export interface StatData {
  count: number
  trend: TrendData | null
}

export interface StatusBreakdownItem {
  status: string
  count: number
  percentage: number
  color: string
}

export interface JobSubmissionData {
  job_uuid: string
  job_id?: string
  job_title: string
  client_name: string
  submitted_count: number
  pending_count: number
  created_by_name?: string | null
  created_at?: string | null
  assigned_recruiters?: string[]
}

export interface SourceBreakdownItem {
  source_name: string
  count: number
}

export interface DashboardData {
  stats: {
    resumes_uploaded: StatData
    jobs_created: StatData
    matches_applied: StatData
  }
  status_breakdown: StatusBreakdownItem[]
  job_submissions: JobSubmissionData[]
  resume_sources: SourceBreakdownItem[]
  job_sources: SourceBreakdownItem[]
  is_empty: boolean
}

export interface UseDashboardParams {
  dateRange: DateRangeOption
  customStartDate?: Date
  customEndDate?: Date
}

export interface UseDashboardReturn {
  data: DashboardData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useDashboard = (params: UseDashboardParams): UseDashboardReturn => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { can, isBypassAll, capabilitiesLoading } = usePermission()

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Detect user's timezone from browser
      let userTimezone = 'UTC'
      try {
        userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (e) {
        console.warn('Failed to detect timezone, using UTC:', e)
      }

      // Build query parameters
      const queryParams = new URLSearchParams({
        date_range: params.dateRange,
        timezone: userTimezone
      })

      // Add custom dates if provided
      if (params.dateRange === 'custom' && params.customStartDate && params.customEndDate) {
        queryParams.append('custom_start_date', params.customStartDate.toISOString())
        queryParams.append('custom_end_date', params.customEndDate.toISOString())
      }

      // Managers see team-level stats; everyone else sees personal stats.
      // Converted 2026-08-14 (§10.1) onto the real backend capability —
      // `analytics.teamDashboard` is itself seeded as an exact-manager
      // grant (the backing endpoint resolves the team by the caller's own
      // manager_id, so admin/super_admin would get an empty team back, not
      // real data — RBAC_UI_IMPLEMENTATION_PLAN.md §5.6.A). `can()` alone
      // isn't exact here: the global bypass-all invariant (permissions.py)
      // deliberately makes every capability check pass for a bypass-all
      // role like Admin — correct for access control (Admin may still open
      // /team-analytics directly), but wrong for this "which data feeds MY
      // dashboard" branch, since Admin manages no real team. Excluding
      // bypass-all only at this decision point (not from can() itself)
      // restores "everyone but an actual manager sees personal stats" here.
      const usesTeamDashboard = !isBypassAll && can('analytics.teamDashboard')
      const response = usesTeamDashboard
        ? await analyticsApi.getTeamDashboardStats(queryParams.toString())
        : await analyticsApi.getDashboardStats(queryParams.toString())
      setData(response.data)
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err)
      setError(err.response?.data?.detail || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const refetch = async () => {
    await fetchDashboardData()
  }

  useEffect(() => {
    // Wait for the live capability set (`GET /auth/me/capabilities`) to
    // resolve before picking an endpoint. Firing immediately on mount would
    // sometimes decide `usesTeamDashboard` from a still-empty/default
    // capability state — e.g. right after in-app navigation, before that
    // request settles — and this hook never re-decides afterward, so a
    // wrong one-time choice stuck until the next full reload.
    if (capabilitiesLoading) return
    fetchDashboardData()
  }, [params.dateRange, params.customStartDate, params.customEndDate, capabilitiesLoading, isBypassAll])

  return {
    data,
    loading,
    error,
    refetch
  }
}
